import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { 
  Plus, 
  FileText, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Upload,
  Eye,
  Send,
  Building2,
  Calendar,
  IndianRupee,
  Package,
  Zap,
  Home,
  AlertCircle,
  Loader2,
  Trash2,
  Camera,
  Download
} from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage, formatCurrency } from '@/lib/utils';

import { BACKEND_URL } from '@/lib/api';
const API = `${BACKEND_URL}/api`;

const CreditLedger = ({ user }) => {
  const [vendors, setVendors] = useState([]);
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [summary, setSummary] = useState({ total_pending: 0, total_billed: 0, total_paid: 0, pending_count: 0 });
  
  // Utility Bills State
  const [utilityBills, setUtilityBills] = useState([]);
  const [utilitySummary, setUtilitySummary] = useState({ total_pending: 0, total_amount: 0, total_paid: 0 });
  const [showUtilityPrompt, setShowUtilityPrompt] = useState(false);
  const [pendingUtilityTypes, setPendingUtilityTypes] = useState([]);
  const [newUtilityBills, setNewUtilityBills] = useState({ 
    rent_manpreet: '', 
    rent_gavvy: '', 
    electricity: '',
    gst: '',
    vat: '',
    monthly_excise: '',
    monthly_pos: ''
  });
  const [currentMonth, setCurrentMonth] = useState('');
  const [selectedUtilityMonth, setSelectedUtilityMonth] = useState(''); // For backdated utility bills
  
  // New bill form
  const [showNewBill, setShowNewBill] = useState(false);
  const [creatingBill, setCreatingBill] = useState(false);
  const [newBill, setNewBill] = useState({
    vendor_id: '',
    bill_number: '',
    bill_date: new Date().toISOString().split('T')[0],
    due_date: '',
    amount: '',
    remarks: ''
  });
  const [billPhoto, setBillPhoto] = useState(null);
  const [productPhoto, setProductPhoto] = useState(null);
  
  // View bill dialog
  const [selectedBill, setSelectedBill] = useState(null);
  const [showBillDetails, setShowBillDetails] = useState(false);
  
  // Reject dialog
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectBillId, setRejectBillId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  
  // New vendor dialog
  const [showNewVendor, setShowNewVendor] = useState(false);
  const [newVendor, setNewVendor] = useState({ name: '', contact_person: '', phone: '', address: '' });
  
  // Vendor-wise Ledger State
  const [vendorLedgerFilter, setVendorLedgerFilter] = useState('this_month');
  const [vendorLedgerCustomStart, setVendorLedgerCustomStart] = useState('');
  const [vendorLedgerCustomEnd, setVendorLedgerCustomEnd] = useState('');
  const [vendorSummaries, setVendorSummaries] = useState([]);
  const [selectedVendorLedger, setSelectedVendorLedger] = useState(null);
  const [vendorLedgerDetails, setVendorLedgerDetails] = useState({ bills: [], payments: [] });
  const [loadingVendorLedger, setLoadingVendorLedger] = useState(false);

  const canCreateBills = ['storekeeper', 'admin', 'general_manager'].includes(user?.role);
  const canApprove = ['general_manager', 'admin', 'accountant'].includes(user?.role);
  const canManageVendors = ['admin', 'general_manager'].includes(user?.role);
  const canManageUtility = ['admin', 'accountant', 'general_manager'].includes(user?.role);
  const canExport = ['admin', 'general_manager', 'accountant', 'partner'].includes(user?.role);

  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportType, setExportType] = useState('summary'); // 'summary' or 'full_month'

  const handleExportPdf = async (type = 'summary') => {
    try {
      setExportingPdf(true);
      const token = localStorage.getItem('token');
      
      // Helper function to format date as YYYY-MM-DD in local timezone
      const formatDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      
      // Build query params based on current vendor ledger filter
      let params = new URLSearchParams();
      const today = new Date();
      
      if (vendorLedgerFilter === 'custom' && vendorLedgerCustomStart && vendorLedgerCustomEnd) {
        params.set('start_date', vendorLedgerCustomStart);
        params.set('end_date', vendorLedgerCustomEnd);
      } else if (vendorLedgerFilter === 'today') {
        params.set('start_date', formatDate(today));
        params.set('end_date', formatDate(today));
      } else if (vendorLedgerFilter === 'yesterday') {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        params.set('start_date', formatDate(yesterday));
        params.set('end_date', formatDate(yesterday));
      } else if (vendorLedgerFilter === 'last_7_days') {
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        params.set('start_date', formatDate(weekAgo));
        params.set('end_date', formatDate(today));
      } else if (vendorLedgerFilter === 'this_month') {
        params.set('start_date', `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`);
        params.set('end_date', formatDate(today));
      } else if (vendorLedgerFilter === 'last_month') {
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        params.set('start_date', formatDate(lastMonth));
        params.set('end_date', formatDate(lastMonthEnd));
      } else if (vendorLedgerFilter === 'total_business') {
        params.set('start_date', '2020-01-01');
        params.set('end_date', formatDate(today));
      }
      
      // Choose endpoint based on export type
      const endpoint = type === 'full_month' 
        ? `${API}/reports/credit-ledger/export-full-month-pdf`
        : `${API}/reports/credit-ledger/export-pdf`;
      
      const response = await axios.get(
        `${endpoint}?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const exportSuffix = type === 'full_month' ? 'Full_Month' : 'Summary';
      link.setAttribute('download', `Credit_Ledger_${exportSuffix}_${formatDate(today)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF exported successfully');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to export PDF'));
    } finally {
      setExportingPdf(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const [vendorsRes, billsRes, allBillsRes, utilityRes] = await Promise.all([
        axios.get(`${API}/vendors`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/purchase-bills`, { 
          headers: { Authorization: `Bearer ${token}` },
          params: activeTab !== 'all' ? { status: activeTab } : {}
        }),
        // Fetch all bills for summary calculation
        axios.get(`${API}/purchase-bills`, { headers: { Authorization: `Bearer ${token}` } }),
        // Fetch utility bills
        axios.get(`${API}/utility-bills`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setVendors(vendorsRes.data);
      
      // Get utility bills and mark them
      const utilityBillsData = (utilityRes.data.bills || []).map(ub => ({
        ...ub,
        isUtility: true,
        bill_number: `${ub.bill_type_label}`,
        vendor_name: `Utility - ${ub.bill_type_label}`,
        bill_date: `${ub.bill_month}-01` // Convert month to date for sorting
      }));
      setUtilityBills(utilityBillsData);
      setUtilitySummary(utilityRes.data.summary || { total_pending: 0, total_amount: 0, total_paid: 0 });
      
      // Filter utility bills based on active tab
      let filteredUtilityBills = utilityBillsData;
      if (activeTab === 'paid') {
        filteredUtilityBills = utilityBillsData.filter(ub => ub.status === 'paid');
      } else if (activeTab === 'payable' || activeTab === 'approved') {
        filteredUtilityBills = utilityBillsData.filter(ub => ['pending', 'partially_paid'].includes(ub.status));
      } else if (activeTab === 'draft' || activeTab === 'pending_approval') {
        filteredUtilityBills = []; // Utility bills don't have draft/pending_approval status
      }
      
      // Merge purchase bills with utility bills
      const purchaseBills = billsRes.data.map(b => ({ ...b, isUtility: false }));
      const mergedBills = [...purchaseBills, ...filteredUtilityBills].sort((a, b) => 
        new Date(b.bill_date || b.created_at) - new Date(a.bill_date || a.created_at)
      );
      setBills(mergedBills);
      
      // Calculate summary from all bills (purchase + utility)
      const allPurchaseBills = allBillsRes.data;
      
      // Total Billed = sum of all bill amounts
      const purchaseBilled = allPurchaseBills.reduce((sum, b) => sum + (b.amount || 0), 0);
      
      // Total Paid = sum of all payments made
      const purchasePaid = allPurchaseBills.reduce((sum, b) => sum + ((b.amount || 0) - (b.balance || 0)), 0);
      
      // Total Pending = ONLY approved and partially_paid bills (not drafts or pending_approval)
      const approvedBills = allPurchaseBills.filter(b => ['approved', 'partially_paid'].includes(b.status));
      const purchasePending = approvedBills.reduce((sum, b) => sum + (b.balance || 0), 0);
      const payableBillsCount = approvedBills.length;
      
      // Add utility totals (utility bills are always "approved" once created)
      const utilSummary = utilityRes.data.summary || { total_pending: 0, total_amount: 0, total_paid: 0, pending_count: 0 };
      
      // Total Billed includes all bills, but Total Pending only includes approved bills
      const totalBilled = purchaseBilled + (utilSummary.total_amount || 0);
      const totalPaid = purchasePaid + (utilSummary.total_paid || 0);
      
      setSummary({
        total_pending: purchasePending + (utilSummary.total_pending || 0),  // Only approved bills
        total_billed: totalBilled,
        total_paid: totalPaid,
        pending_count: payableBillsCount + (utilSummary.pending_count || 0)
      });
      
      setLoading(false);
    } catch (error) {
      console.error('Credit Ledger fetch error:', error);
      const errorMsg = error.message || 'Failed to fetch data';
      toast.error(errorMsg, {
        action: {
          label: 'Retry',
          onClick: () => fetchData()
        }
      });
      setLoading(false);
    }
  };

  // Check for pending utility entry on mount
  const checkUtilityEntry = async () => {
    if (!canManageUtility) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/utility-bills/pending-entry`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.needs_entry) {
        setPendingUtilityTypes(response.data.pending_types);
        setCurrentMonth(response.data.current_month);
        setShowUtilityPrompt(true);
      }
    } catch (error) {
      console.error('Failed to check utility entry:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  useEffect(() => {
    checkUtilityEntry();
  }, []);

  // Helper function to format date as YYYY-MM-DD in local timezone (avoids UTC shift issues)
  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Fetch Vendor-wise Ledger
  const fetchVendorLedger = async () => {
    setLoadingVendorLedger(true);
    try {
      const token = localStorage.getItem('token');
      
      // Calculate date range based on filter
      const today = new Date();
      let startDate, endDate;
      
      switch (vendorLedgerFilter) {
        case 'today':
          startDate = endDate = formatLocalDate(today);
          break;
        case 'yesterday':
          const yesterday = new Date(today);
          yesterday.setDate(yesterday.getDate() - 1);
          startDate = endDate = formatLocalDate(yesterday);
          break;
        case 'last_7_days':
          const weekAgo = new Date(today);
          weekAgo.setDate(weekAgo.getDate() - 7);
          startDate = formatLocalDate(weekAgo);
          endDate = formatLocalDate(today);
          break;
        case 'this_month':
          startDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;
          endDate = formatLocalDate(today);
          break;
        case 'last_month':
          const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
          startDate = formatLocalDate(lastMonth);
          endDate = formatLocalDate(lastMonthEnd);
          break;
        case 'total_business':
          // All time - use a very early start date
          startDate = '2020-01-01';
          endDate = formatLocalDate(today);
          break;
        case 'custom':
          startDate = vendorLedgerCustomStart;
          endDate = vendorLedgerCustomEnd;
          break;
        default:
          startDate = endDate = formatLocalDate(today);
      }
      
      if (!startDate || !endDate) {
        setLoadingVendorLedger(false);
        return;
      }
      
      const response = await axios.get(`${API}/reports/vendor-ledger-summary`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { start_date: startDate, end_date: endDate }
      });
      
      setVendorSummaries(response.data.vendors || []);
    } catch (error) {
      toast.error('Failed to fetch vendor ledger');
    } finally {
      setLoadingVendorLedger(false);
    }
  };
  
  // Fetch detailed ledger for a specific vendor
  const fetchVendorDetails = async (vendorId, vendorName) => {
    try {
      const token = localStorage.getItem('token');
      
      const today = new Date();
      let startDate, endDate;
      
      switch (vendorLedgerFilter) {
        case 'today':
          startDate = endDate = formatLocalDate(today);
          break;
        case 'yesterday':
          const yesterday = new Date(today);
          yesterday.setDate(yesterday.getDate() - 1);
          startDate = endDate = formatLocalDate(yesterday);
          break;
        case 'last_7_days':
          const weekAgo = new Date(today);
          weekAgo.setDate(weekAgo.getDate() - 7);
          startDate = formatLocalDate(weekAgo);
          endDate = formatLocalDate(today);
          break;
        case 'this_month':
          startDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;
          endDate = formatLocalDate(today);
          break;
        case 'last_month':
          const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
          startDate = formatLocalDate(lastMonth);
          endDate = formatLocalDate(lastMonthEnd);
          break;
        case 'total_business':
          // All time - use a very early start date
          startDate = '2020-01-01';
          endDate = formatLocalDate(today);
          break;
        case 'custom':
          startDate = vendorLedgerCustomStart;
          endDate = vendorLedgerCustomEnd;
          break;
        default:
          startDate = endDate = formatLocalDate(today);
      }
      
      const response = await axios.get(`${API}/reports/vendor-ledger/${vendorId}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { start_date: startDate, end_date: endDate }
      });
      
      setVendorLedgerDetails(response.data);
      setSelectedVendorLedger({ id: vendorId, name: vendorName });
    } catch (error) {
      toast.error('Failed to fetch vendor details');
    }
  };

  useEffect(() => {
    if (activeTab === 'vendor_ledger') {
      fetchVendorLedger();
    }
  }, [activeTab, vendorLedgerFilter, vendorLedgerCustomStart, vendorLedgerCustomEnd]);

  // Handle utility bills entry
  const handleSubmitUtilityBills = async () => {
    try {
      const token = localStorage.getItem('token');
      let created = 0;
      const monthToUse = selectedUtilityMonth || currentMonth;
      
      for (const pending of pendingUtilityTypes) {
        const amount = parseFloat(newUtilityBills[pending.type]);
        if (amount > 0) {
          await axios.post(`${API}/utility-bills`, {
            bill_type: pending.type,
            bill_month: monthToUse,
            amount: amount
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          created++;
        }
      }
      
      if (created > 0) {
        toast.success(`${created} utility bill(s) created for ${monthToUse}`);
        setShowUtilityPrompt(false);
        setSelectedUtilityMonth(''); // Reset selected month
        setNewUtilityBills({ 
          rent_manpreet: '', 
          rent_gavvy: '', 
          electricity: '',
          gst: '',
          vat: '',
          monthly_excise: '',
          monthly_pos: ''
        });
        fetchData();
      } else {
        toast.error('Please enter at least one amount');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create utility bills'));
    }
  };

  const handleCreateBill = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (creatingBill) return;
    
    // Validate mandatory bill photo
    if (!billPhoto) {
      toast.error('Bill photo is required. Please upload an image of the bill.');
      return;
    }
    
    setCreatingBill(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      
      formData.append('vendor_id', newBill.vendor_id);
      formData.append('bill_number', newBill.bill_number);
      formData.append('bill_date', newBill.bill_date);
      formData.append('amount', newBill.amount);
      if (newBill.due_date) formData.append('due_date', newBill.due_date);
      if (newBill.remarks) formData.append('remarks', newBill.remarks);
      formData.append('bill_photo', billPhoto); // Now mandatory
      if (productPhoto) formData.append('product_photo', productPhoto);
      
      await axios.post(`${API}/purchase-bills`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('Bill created successfully');
      setShowNewBill(false);
      setNewBill({ vendor_id: '', bill_number: '', bill_date: new Date().toISOString().split('T')[0], due_date: '', amount: '', remarks: '' });
      setBillPhoto(null);
      setProductPhoto(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create bill'));
    } finally {
      setCreatingBill(false);
    }
  };

  const handleSubmitBill = async (billId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/purchase-bills/${billId}/submit`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Bill submitted for approval');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to submit bill'));
    }
  };

  const handleApproveBill = async (billId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/purchase-bills/${billId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Bill approved');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to approve bill'));
    }
  };

  const handleRejectBill = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/purchase-bills/${rejectBillId}/reject?reason=${encodeURIComponent(rejectReason)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Bill rejected');
      setShowRejectDialog(false);
      setRejectBillId(null);
      setRejectReason('');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to reject bill'));
    }
  };

  const handleDeleteBill = async (billId, billStatus) => {
    const warningMessage = billStatus === 'approved' || billStatus === 'partially_paid' || billStatus === 'paid'
      ? 'WARNING: This bill has been approved/paid. Deleting it will also remove all associated payments. Are you sure you want to permanently delete this bill? This action cannot be undone.'
      : 'Are you sure you want to permanently delete this bill? This action cannot be undone.';
    
    if (!window.confirm(warningMessage)) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API}/purchase-bills/${billId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const paymentsDeleted = response.data?.payments_deleted || 0;
      toast.success(paymentsDeleted > 0 
        ? `Bill deleted successfully (${paymentsDeleted} payment(s) also removed)` 
        : 'Bill deleted successfully');
      setShowBillDetails(false);
      setSelectedBill(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete bill'));
    }
  };

  const handleCreateVendor = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/vendors`, newVendor, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Vendor created');
      setShowNewVendor(false);
      setNewVendor({ name: '', contact_person: '', phone: '', address: '' });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create vendor'));
    }
  };

  const viewBillDetails = async (billId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/purchase-bills/${billId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedBill(response.data);
      setShowBillDetails(true);
    } catch (error) {
      toast.error('Failed to load bill details');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-700',
      submitted: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
      partially_paid: 'bg-blue-100 text-blue-700',
      paid: 'bg-emerald-100 text-emerald-700',
      cancelled: 'bg-gray-200 text-gray-500',
      pending: 'bg-orange-100 text-orange-700'
    };
    return <Badge className={styles[status] || 'bg-gray-100'}>{status.replace('_', ' ').toUpperCase()}</Badge>;
  };

  const getUtilityIcon = (type) => {
    return type.startsWith('rent') ? <Home className="w-4 h-4" /> : <Zap className="w-4 h-4" />;
  };

  return (
    <div className="space-y-6" data-testid="credit-ledger-page">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
          Credit Ledger / Purchase Bills
        </h1>
        <div className="flex gap-2">
          {canManageVendors && (
            <Dialog open={showNewVendor} onOpenChange={setShowNewVendor}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="add-vendor-btn">
                  <Building2 className="w-4 h-4 mr-2" />
                  Add Vendor
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New Vendor</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateVendor} className="space-y-4">
                  <div>
                    <Label>Vendor Name *</Label>
                    <Input 
                      value={newVendor.name} 
                      onChange={(e) => setNewVendor({...newVendor, name: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label>Contact Person</Label>
                    <Input 
                      value={newVendor.contact_person} 
                      onChange={(e) => setNewVendor({...newVendor, contact_person: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input 
                      value={newVendor.phone} 
                      onChange={(e) => setNewVendor({...newVendor, phone: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Address</Label>
                    <Textarea 
                      value={newVendor.address} 
                      onChange={(e) => setNewVendor({...newVendor, address: e.target.value})}
                    />
                  </div>
                  <Button type="submit" className="w-full">Create Vendor</Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
          
          {canManageUtility && (
            <Button 
              variant="outline"
              onClick={() => {
                setCurrentMonth(new Date().toISOString().slice(0, 7));
                setPendingUtilityTypes([
                  { type: 'rent_manpreet', label: 'Rent Manpreet' },
                  { type: 'rent_gavvy', label: 'Rent Gavvy' },
                  { type: 'electricity', label: 'PSPCL/Electricity' },
                  { type: 'gst', label: 'GST' },
                  { type: 'vat', label: 'VAT' },
                  { type: 'monthly_excise', label: 'Monthly-Excise' },
                  { type: 'monthly_pos', label: 'Monthly-POS' }
                ]);
                setShowUtilityPrompt(true);
              }}
              data-testid="add-utility-btn"
            >
              <Zap className="w-4 h-4 mr-2" />
              Add Utility Bill
            </Button>
          )}
          
          {canCreateBills && (
            <Dialog open={showNewBill} onOpenChange={setShowNewBill}>
              <DialogTrigger asChild>
                <Button data-testid="add-bill-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Bill
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Add Purchase Bill</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateBill} className="space-y-4">
                  <div>
                    <Label>Vendor *</Label>
                    <Select value={newBill.vendor_id} onValueChange={(v) => setNewBill({...newBill, vendor_id: v})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select vendor" />
                      </SelectTrigger>
                      <SelectContent>
                        {vendors.filter(v => v.is_active === true).map(v => (
                          <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Bill Number *</Label>
                      <Input 
                        value={newBill.bill_number} 
                        onChange={(e) => setNewBill({...newBill, bill_number: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label>Amount *</Label>
                      <Input 
                        type="number"
                        step="0.01"
                        value={newBill.amount} 
                        onChange={(e) => setNewBill({...newBill, amount: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Bill Date *</Label>
                      <Input 
                        type="date"
                        value={newBill.bill_date} 
                        onChange={(e) => setNewBill({...newBill, bill_date: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label>Due Date</Label>
                      <Input 
                        type="date"
                        value={newBill.due_date} 
                        onChange={(e) => setNewBill({...newBill, due_date: e.target.value})}
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="flex items-center gap-1">
                      Bill Photo <span className="text-red-500">*</span>
                    </Label>
                    <div className={`border-2 border-dashed rounded-lg p-4 text-center ${billPhoto ? 'border-green-400 bg-green-50' : 'border-red-300 bg-red-50'}`}>
                      <Input 
                        type="file"
                        accept="image/*"
                        onChange={(e) => setBillPhoto(e.target.files[0])}
                        className="hidden"
                        id="bill-photo-input"
                        required
                      />
                      <label htmlFor="bill-photo-input" className="cursor-pointer">
                        {billPhoto ? (
                          <div className="flex items-center justify-center gap-2 text-green-600">
                            <CheckCircle className="w-5 h-5" />
                            <span>{billPhoto.name}</span>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center gap-2 text-red-600">
                            <Camera className="w-8 h-8" />
                            <span className="text-sm font-medium">Click to upload bill image (Required)</span>
                          </div>
                        )}
                      </label>
                    </div>
                  </div>
                  <div>
                    <Label>Product Photo (Optional)</Label>
                    <Input 
                      type="file"
                      accept="image/*"
                      onChange={(e) => setProductPhoto(e.target.files[0])}
                    />
                  </div>
                  <div>
                    <Label>Remarks</Label>
                    <Textarea 
                      value={newBill.remarks} 
                      onChange={(e) => setNewBill({...newBill, remarks: e.target.value})}
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={creatingBill}>
                    {creatingBill ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      'Create Bill'
                    )}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-red-50 border-red-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              <IndianRupee className="w-4 h-4 text-red-600" />
              <span className="text-sm text-red-600 font-medium">Total Pending</span>
            </div>
            <p className="text-2xl font-bold text-red-700">{formatCurrency(summary.total_pending)}</p>
            <p className="text-xs text-red-500">{summary.pending_count} bills pending</p>
          </CardContent>
        </Card>
        
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              <FileText className="w-4 h-4 text-blue-600" />
              <span className="text-sm text-blue-600 font-medium">Total Billed</span>
            </div>
            <p className="text-2xl font-bold text-blue-700">{formatCurrency(summary.total_billed)}</p>
            <p className="text-xs text-blue-500">{bills.length} total bills</p>
          </CardContent>
        </Card>
        
        <Card className="bg-green-50 border-green-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span className="text-sm text-green-600 font-medium">Total Paid</span>
            </div>
            <p className="text-2xl font-bold text-green-700">{formatCurrency(summary.total_paid)}</p>
          </CardContent>
        </Card>
        
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-4 h-4 text-amber-600" />
              <span className="text-sm text-amber-600 font-medium">Pending Approval</span>
            </div>
            <p className="text-2xl font-bold text-amber-700">
              {bills.filter(b => b.status === 'submitted').length}
            </p>
            <p className="text-xs text-amber-500">bills awaiting approval</p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-wrap h-auto gap-1">
          <TabsTrigger value="all">All Bills</TabsTrigger>
          <TabsTrigger value="draft">Drafts</TabsTrigger>
          <TabsTrigger value="pending_approval">Pending Approval</TabsTrigger>
          <TabsTrigger value="payable">Payable</TabsTrigger>
          <TabsTrigger value="paid">Paid</TabsTrigger>
          <TabsTrigger value="vendor_ledger" className="bg-blue-100 text-blue-700 font-medium">Vendor Ledger</TabsTrigger>
        </TabsList>

        {/* Vendor Ledger Tab */}
        <TabsContent value="vendor_ledger" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  {selectedVendorLedger ? `${selectedVendorLedger.name} - Ledger` : 'Vendor-wise Ledger'}
                </CardTitle>
                <div className="flex flex-wrap items-center gap-2">
                  {canExport && (
                    <div className="flex gap-1">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleExportPdf('summary')}
                        disabled={exportingPdf}
                        title="Export only vendors with transactions in period"
                      >
                        {exportingPdf ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4 mr-2" />
                        )}
                        Export Summary
                      </Button>
                      <Button 
                        variant="default" 
                        size="sm" 
                        onClick={() => handleExportPdf('full_month')}
                        disabled={exportingPdf}
                        title="Export ALL vendors including those with 0 balance"
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        {exportingPdf ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <FileText className="w-4 h-4 mr-2" />
                        )}
                        Full Month Details
                      </Button>
                    </div>
                  )}
                  {selectedVendorLedger && (
                    <Button variant="outline" size="sm" onClick={() => setSelectedVendorLedger(null)}>
                      ← Back to All Vendors
                    </Button>
                  )}
                  <Select value={vendorLedgerFilter} onValueChange={setVendorLedgerFilter}>
                    <SelectTrigger className="w-[150px]">
                      <SelectValue placeholder="Select Period" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="today">Today</SelectItem>
                      <SelectItem value="yesterday">Yesterday</SelectItem>
                      <SelectItem value="last_7_days">Last 7 Days</SelectItem>
                      <SelectItem value="this_month">This Month</SelectItem>
                      <SelectItem value="last_month">Last Month</SelectItem>
                      <SelectItem value="total_business">Total Business</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                  {vendorLedgerFilter === 'custom' && (
                    <>
                      <Input
                        type="date"
                        value={vendorLedgerCustomStart}
                        onChange={(e) => setVendorLedgerCustomStart(e.target.value)}
                        className="w-[140px]"
                      />
                      <span>to</span>
                      <Input
                        type="date"
                        value={vendorLedgerCustomEnd}
                        onChange={(e) => setVendorLedgerCustomEnd(e.target.value)}
                        className="w-[140px]"
                      />
                    </>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loadingVendorLedger ? (
                <div className="text-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                  <p className="mt-2 text-gray-500">Loading vendor ledger...</p>
                </div>
              ) : selectedVendorLedger ? (
                /* Vendor Details View */
                <div className="space-y-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <p className="text-sm text-blue-600">Total Bills</p>
                      <p className="text-2xl font-bold">{vendorLedgerDetails.summary?.total_bills || 0}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600">Total Billed</p>
                      <p className="text-2xl font-bold">₹{(vendorLedgerDetails.summary?.total_billed || 0).toLocaleString('en-IN')}</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <p className="text-sm text-green-600">Total Paid</p>
                      <p className="text-2xl font-bold">₹{(vendorLedgerDetails.summary?.total_paid || 0).toLocaleString('en-IN')}</p>
                    </div>
                    <div className="bg-red-50 p-4 rounded-lg">
                      <p className="text-sm text-red-600">Pending</p>
                      <p className="text-2xl font-bold">₹{(vendorLedgerDetails.summary?.total_pending || 0).toLocaleString('en-IN')}</p>
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Bills */}
                    <div>
                      <h3 className="font-semibold mb-3 flex items-center gap-2">
                        <FileText className="w-4 h-4" /> Bills Received
                      </h3>
                      <div className="border rounded-lg overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Date</TableHead>
                              <TableHead>Bill #</TableHead>
                              <TableHead className="text-right">Amount</TableHead>
                              <TableHead className="text-right">Balance</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {vendorLedgerDetails.bills?.length === 0 ? (
                              <TableRow><TableCell colSpan={4} className="text-center text-gray-500">No bills</TableCell></TableRow>
                            ) : vendorLedgerDetails.bills?.map(bill => (
                              <TableRow key={bill.id}>
                                <TableCell>{bill.bill_date}</TableCell>
                                <TableCell>{bill.bill_number}</TableCell>
                                <TableCell className="text-right">₹{bill.amount.toLocaleString('en-IN')}</TableCell>
                                <TableCell className="text-right text-red-600">₹{bill.balance.toLocaleString('en-IN')}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                    
                    {/* Payments */}
                    <div>
                      <h3 className="font-semibold mb-3 flex items-center gap-2">
                        <IndianRupee className="w-4 h-4" /> Payments Made
                      </h3>
                      <div className="border rounded-lg overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Date</TableHead>
                              <TableHead>Bill #</TableHead>
                              <TableHead className="text-right">Amount</TableHead>
                              <TableHead>Method</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {vendorLedgerDetails.payments?.length === 0 ? (
                              <TableRow><TableCell colSpan={4} className="text-center text-gray-500">No payments</TableCell></TableRow>
                            ) : vendorLedgerDetails.payments?.map(payment => (
                              <TableRow key={payment.id}>
                                <TableCell>{payment.paid_at?.split('T')[0] || '-'}</TableCell>
                                <TableCell>{payment.bill_number}</TableCell>
                                <TableCell className="text-right text-green-600">₹{payment.amount.toLocaleString('en-IN')}</TableCell>
                                <TableCell>{payment.payment_method || 'cash'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* Vendor Summary View */
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Vendor Name</TableHead>
                      <TableHead className="text-right">Bills Received</TableHead>
                      <TableHead className="text-right">Total Billed</TableHead>
                      <TableHead className="text-right">Total Paid</TableHead>
                      <TableHead className="text-right">Pending</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {vendorSummaries.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                          No vendor data for selected period
                        </TableCell>
                      </TableRow>
                    ) : (
                      vendorSummaries.map(vendor => (
                        <TableRow 
                          key={vendor.vendor_id} 
                          className="cursor-pointer hover:bg-gray-50"
                          onClick={() => fetchVendorDetails(vendor.vendor_id, vendor.vendor_name)}
                        >
                          <TableCell className="font-medium text-blue-600 hover:underline">
                            {vendor.vendor_name}
                          </TableCell>
                          <TableCell className="text-right">{vendor.total_bills}</TableCell>
                          <TableCell className="text-right">₹{vendor.total_billed.toLocaleString('en-IN')}</TableCell>
                          <TableCell className="text-right text-green-600">₹{vendor.total_paid.toLocaleString('en-IN')}</TableCell>
                          <TableCell className="text-right text-red-600">₹{vendor.total_pending.toLocaleString('en-IN')}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {activeTab !== 'vendor_ledger' && (
        <TabsContent value={activeTab} className="mt-4">
          <Card>
            <CardContent className="pt-6">
              {loading ? (
                <div className="text-center py-8">Loading...</div>
              ) : bills.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No bills found</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Bill No. / Description</TableHead>
                      <TableHead>Vendor</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bills.map((bill) => (
                      <TableRow key={bill.id} className={bill.isUtility ? 'bg-purple-50/50' : ''}>
                        <TableCell>
                          {bill.isUtility 
                            ? bill.bill_month 
                            : new Date(bill.bill_date).toLocaleDateString('en-IN')
                          }
                        </TableCell>
                        <TableCell>
                          {bill.isUtility ? (
                            <div className="flex items-center gap-1">
                              {getUtilityIcon(bill.bill_type)}
                              <Badge variant="outline" className="bg-purple-100 text-purple-700 border-purple-300">
                                Utility
                              </Badge>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1">
                              <Package className="w-4 h-4 text-blue-600" />
                              <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-300">
                                Purchase
                              </Badge>
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="font-medium">
                          {bill.isUtility ? bill.bill_type_label : bill.bill_number}
                        </TableCell>
                        <TableCell>
                          {bill.isUtility ? `Utility - ${bill.bill_type_label}` : bill.vendor_name}
                        </TableCell>
                        <TableCell className="text-right">{formatCurrency(bill.amount)}</TableCell>
                        <TableCell className="text-right font-semibold text-red-600">
                          {formatCurrency(bill.balance)}
                        </TableCell>
                        <TableCell>{getStatusBadge(bill.status)}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                if (bill.isUtility) {
                                  setSelectedBill({ ...bill, isUtility: true });
                                  setShowBillDetails(true);
                                } else {
                                  viewBillDetails(bill.id);
                                }
                              }}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            
                            {!bill.isUtility && bill.status === 'draft' && canCreateBills && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleSubmitBill(bill.id)}
                                title="Submit for approval"
                              >
                                <Send className="w-4 h-4 text-blue-600" />
                              </Button>
                            )}
                            
                            {!bill.isUtility && bill.status === 'submitted' && canApprove && (
                              <>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => handleApproveBill(bill.id)}
                                  title="Approve"
                                >
                                  <CheckCircle className="w-4 h-4 text-green-600" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => { setRejectBillId(bill.id); setShowRejectDialog(true); }}
                                  title="Reject"
                                >
                                  <XCircle className="w-4 h-4 text-red-600" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        )}
      </Tabs>

      {/* Bill Details Dialog */}
      <Dialog open={showBillDetails} onOpenChange={setShowBillDetails}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {selectedBill?.isUtility 
                ? `${selectedBill?.bill_type_label} - ${selectedBill?.bill_month}`
                : `Bill Details - ${selectedBill?.bill_number}`
              }
            </DialogTitle>
          </DialogHeader>
          {selectedBill && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {!selectedBill.isUtility && (
                  <div>
                    <Label className="text-gray-500">Vendor</Label>
                    <p className="font-medium">{selectedBill.vendor_name}</p>
                  </div>
                )}
                {selectedBill.isUtility && (
                  <div>
                    <Label className="text-gray-500">Type</Label>
                    <p className="font-medium flex items-center gap-2">
                      {getUtilityIcon(selectedBill.bill_type)}
                      {selectedBill.bill_type_label}
                    </p>
                  </div>
                )}
                <div>
                  <Label className="text-gray-500">Status</Label>
                  <p>{getStatusBadge(selectedBill.status)}</p>
                </div>
                {!selectedBill.isUtility && (
                  <>
                    <div>
                      <Label className="text-gray-500">Bill Date</Label>
                      <p>{new Date(selectedBill.bill_date).toLocaleDateString('en-IN')}</p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Due Date</Label>
                      <p>{selectedBill.due_date ? new Date(selectedBill.due_date).toLocaleDateString('en-IN') : '-'}</p>
                    </div>
                  </>
                )}
                {selectedBill.isUtility && (
                  <div>
                    <Label className="text-gray-500">Month</Label>
                    <p>{selectedBill.bill_month}</p>
                  </div>
                )}
                <div>
                  <Label className="text-gray-500">Amount</Label>
                  <p className="font-bold text-lg">{formatCurrency(selectedBill.amount)}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Balance</Label>
                  <p className="font-bold text-lg text-red-600">{formatCurrency(selectedBill.balance)}</p>
                </div>
              </div>
              
              {selectedBill.remarks && (
                <div>
                  <Label className="text-gray-500">Remarks</Label>
                  <p>{selectedBill.remarks}</p>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                {selectedBill.bill_photo_url && (
                  <div>
                    <Label className="text-gray-500">Bill Photo</Label>
                    <img 
                      src={selectedBill.bill_photo_url.startsWith('http') 
                        ? selectedBill.bill_photo_url 
                        : `${BACKEND_URL}${selectedBill.bill_photo_url}`} 
                      alt="Bill" 
                      className="mt-2 max-h-48 rounded border"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                      }}
                    />
                    <div className="mt-2 h-48 bg-gray-100 rounded border items-center justify-center text-gray-400 hidden">
                      <span className="text-sm">Image not available</span>
                    </div>
                  </div>
                )}
                {selectedBill.product_photo_url && (
                  <div>
                    <Label className="text-gray-500">Product Photo</Label>
                    <img 
                      src={selectedBill.product_photo_url.startsWith('http') 
                        ? selectedBill.product_photo_url 
                        : `${BACKEND_URL}${selectedBill.product_photo_url}`} 
                      alt="Product" 
                      className="mt-2 max-h-48 rounded border"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                      }}
                    />
                    <div className="mt-2 h-48 bg-gray-100 rounded border items-center justify-center text-gray-400 hidden">
                      <span className="text-sm">Image not available</span>
                    </div>
                  </div>
                )}
              </div>
              
              {selectedBill.payments && selectedBill.payments.length > 0 && (
                <div>
                  <Label className="text-gray-500">Payment History</Label>
                  <Table className="mt-2">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Paid By</TableHead>
                        <TableHead>Type</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedBill.payments.map((p) => (
                        <TableRow key={p.id}>
                          <TableCell>{new Date(p.paid_at).toLocaleDateString('en-IN')}</TableCell>
                          <TableCell className="text-green-600">{formatCurrency(p.amount)}</TableCell>
                          <TableCell>{p.paid_by_name}</TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {p.payment_category === 'old_month' ? 'Old Month' : 'Current'}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              
              <div className="text-xs text-gray-400 pt-4 border-t">
                <p>Created by {selectedBill.created_by_name} on {new Date(selectedBill.created_at).toLocaleString('en-IN')}</p>
                {selectedBill.approved_by_name && (
                  <p>Approved by {selectedBill.approved_by_name} on {new Date(selectedBill.approved_at).toLocaleString('en-IN')}</p>
                )}
                {selectedBill.rejected_by_name && (
                  <p className="text-red-500">Rejected by {selectedBill.rejected_by_name}: {selectedBill.rejection_reason}</p>
                )}
              </div>
              
              {/* Delete button - Admin only */}
              {user.role === 'admin' && (
                <div className="pt-4 border-t">
                  <Button 
                    variant="destructive" 
                    onClick={() => handleDeleteBill(selectedBill.id, selectedBill.status)}
                    className="w-full"
                    data-testid="delete-bill-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    {selectedBill.status === 'rejected' ? 'Delete Rejected Bill' : 'Delete Bill'}
                  </Button>
                  {['approved', 'partially_paid', 'paid'].includes(selectedBill.status) && (
                    <p className="text-xs text-red-500 mt-2 text-center">
                      ⚠️ This will also delete all associated payments
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Bill</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Reason for rejection *</Label>
              <Textarea 
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter reason..."
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowRejectDialog(false)}>Cancel</Button>
              <Button variant="destructive" onClick={handleRejectBill}>Reject Bill</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Utility Bills Entry Prompt */}
      <Dialog open={showUtilityPrompt} onOpenChange={(open) => {
        setShowUtilityPrompt(open);
        if (!open) setSelectedUtilityMonth(''); // Reset when closing
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-orange-500" />
              Enter Monthly Utility Bills
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Month Selector */}
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <Calendar className="w-4 h-4 text-blue-600" />
                Select Month
              </Label>
              <Select 
                value={selectedUtilityMonth || currentMonth} 
                onValueChange={setSelectedUtilityMonth}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select month" />
                </SelectTrigger>
                <SelectContent>
                  {/* Generate last 12 months */}
                  {Array.from({ length: 12 }, (_, i) => {
                    const date = new Date();
                    date.setMonth(date.getMonth() - i);
                    const monthStr = date.toISOString().slice(0, 7);
                    const displayStr = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                    return (
                      <SelectItem key={monthStr} value={monthStr}>
                        {displayStr} {i === 0 ? '(Current)' : ''}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-700">
                Enter utility amounts for <strong>{selectedUtilityMonth || currentMonth}</strong>
              </p>
            </div>
            
            {pendingUtilityTypes.some(t => t.type === 'rent_manpreet') && (
              <div>
                <Label className="flex items-center gap-2">
                  <Home className="w-4 h-4 text-purple-600" />
                  Rent Manpreet *
                </Label>
                <Input 
                  type="number"
                  step="0.01"
                  value={newUtilityBills.rent_manpreet}
                  onChange={(e) => setNewUtilityBills({...newUtilityBills, rent_manpreet: e.target.value})}
                  placeholder="Enter Rent Manpreet amount"
                  className="mt-1"
                />
              </div>
            )}
            
            {pendingUtilityTypes.some(t => t.type === 'rent_gavvy') && (
              <div>
                <Label className="flex items-center gap-2">
                  <Home className="w-4 h-4 text-purple-600" />
                  Rent Gavvy *
                </Label>
                <Input 
                  type="number"
                  step="0.01"
                  value={newUtilityBills.rent_gavvy}
                  onChange={(e) => setNewUtilityBills({...newUtilityBills, rent_gavvy: e.target.value})}
                  placeholder="Enter Rent Gavvy amount"
                  className="mt-1"
                />
              </div>
            )}
            
            {pendingUtilityTypes.some(t => t.type === 'electricity') && (
              <div>
                <Label className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-yellow-600" />
                  PSPCL/Electricity Amount *
                </Label>
                <Input 
                  type="number"
                  step="0.01"
                  value={newUtilityBills.electricity}
                  onChange={(e) => setNewUtilityBills({...newUtilityBills, electricity: e.target.value})}
                  placeholder="Enter electricity amount"
                  className="mt-1"
                />
              </div>
            )}

            {pendingUtilityTypes.some(t => t.type === 'gst') && (
              <div>
                <Label className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-green-600" />
                  GST *
                </Label>
                <Input 
                  type="number"
                  step="0.01"
                  value={newUtilityBills.gst || ''}
                  onChange={(e) => setNewUtilityBills({...newUtilityBills, gst: e.target.value})}
                  placeholder="Enter GST amount"
                  className="mt-1"
                />
              </div>
            )}

            {pendingUtilityTypes.some(t => t.type === 'vat') && (
              <div>
                <Label className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-blue-600" />
                  VAT *
                </Label>
                <Input 
                  type="number"
                  step="0.01"
                  value={newUtilityBills.vat || ''}
                  onChange={(e) => setNewUtilityBills({...newUtilityBills, vat: e.target.value})}
                  placeholder="Enter VAT amount"
                  className="mt-1"
                />
              </div>
            )}

            {pendingUtilityTypes.some(t => t.type === 'monthly_excise') && (
              <div>
                <Label className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-red-600" />
                  Monthly-Excise *
                </Label>
                <Input 
                  type="number"
                  step="0.01"
                  value={newUtilityBills.monthly_excise || ''}
                  onChange={(e) => setNewUtilityBills({...newUtilityBills, monthly_excise: e.target.value})}
                  placeholder="Enter Monthly-Excise amount"
                  className="mt-1"
                />
              </div>
            )}

            {pendingUtilityTypes.some(t => t.type === 'monthly_pos') && (
              <div>
                <Label className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-orange-600" />
                  Monthly-POS *
                </Label>
                <Input 
                  type="number"
                  step="0.01"
                  value={newUtilityBills.monthly_pos || ''}
                  onChange={(e) => setNewUtilityBills({...newUtilityBills, monthly_pos: e.target.value})}
                  placeholder="Enter Monthly-POS amount"
                  className="mt-1"
                />
              </div>
            )}
            
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setShowUtilityPrompt(false)}>
                Later
              </Button>
              <Button onClick={handleSubmitUtilityBills}>
                <Plus className="w-4 h-4 mr-2" />
                Create Bills
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreditLedger;
